import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import LeaveCalendarModal from '../LeaveCalendarModal';
import WorkScheduleModal from '../WorkScheduleModal';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import {
  Clock, LogIn, LogOut, QrCode, Users, Timer, AlertCircle,
  RefreshCw, Trash2, Copy, FileSpreadsheet, FileText, Download, BarChart3,
  Edit, Plus, Coffee, Palmtree, CalendarDays, Pencil
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} dk`;
  return `${h} sa ${m} dk`;
};

const AttendancePanel = ({ personnelList = [], isAdmin = false, isSuperAdmin = false }) => {
  const [todayRecords, setTodayRecords] = useState([]);
  const [qrToken, setQrToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  // Admin manuel düzenleme state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null); // { id, personnel_name, date, check_in_HHMM, check_out_HHMM }
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualForm, setManualForm] = useState({ personnel_id: '', date: new Date().toISOString().slice(0, 10), check_in_time: '09:00', check_out_time: '18:00', notes: '' });
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const toHHMM = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return ''; }
  };

  const openEditRecord = (record, personName) => {
    setEditingRecord({
      id: record.id,
      personnel_name: personName,
      date: record.date || new Date().toISOString().slice(0, 10),
      check_in_HHMM: toHHMM(record.check_in_time),
      check_out_HHMM: toHHMM(record.check_out_time),
      notes: record.notes || '',
    });
    setShowEditModal(true);
  };

  const submitEdit = async () => {
    if (!editingRecord) return;
    if (!editingRecord.check_in_HHMM) {
      toast.error('Giriş saati zorunlu'); return;
    }
    setSubmittingEdit(true);
    try {
      await axios.put(`${API}/attendance/${editingRecord.id}/manual`, {
        date: editingRecord.date,
        check_in_time: editingRecord.check_in_HHMM,
        check_out_time: editingRecord.check_out_HHMM || '',
        notes: editingRecord.notes,
      }, { headers: authHeader() });
      toast.success('Kayıt güncellendi');
      setShowEditModal(false);
      setEditingRecord(null);
      await fetchToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const submitManualCreate = async () => {
    if (!manualForm.personnel_id) { toast.error('Personel seçin'); return; }
    if (!manualForm.check_in_time) { toast.error('Giriş saati zorunlu'); return; }
    setSubmittingEdit(true);
    try {
      await axios.post(`${API}/attendance/manual`, {
        personnel_id: manualForm.personnel_id,
        date: manualForm.date,
        check_in_time: manualForm.check_in_time,
        check_out_time: manualForm.check_out_time || null,
        notes: manualForm.notes,
      }, { headers: authHeader() });
      toast.success('Manuel kayıt oluşturuldu');
      setShowManualAddModal(false);
      setManualForm({ personnel_id: '', date: new Date().toISOString().slice(0, 10), check_in_time: '09:00', check_out_time: '18:00', notes: '' });
      await fetchToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Oluşturulamadı');
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Rapor state
  const todayMonth = new Date().toISOString().slice(0, 7);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMonth, setReportMonth] = useState(todayMonth);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('month'); // 'month' | 'week'
  const [reportWeekDate, setReportWeekDate] = useState(new Date().toISOString().slice(0, 10));

  // Gün detay (rapor hücresine tıklama)
  const [dayDetail, setDayDetail] = useState(null); // {row, day}
  const [editingShift, setEditingShift] = useState(null); // {id, in, out, date}
  const [savingShift, setSavingShift] = useState(false);

  // İzinli gün state
  const todayIso = new Date().toISOString().slice(0, 10);
  const [leaves, setLeaves] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showLeaveCalendar, setShowLeaveCalendar] = useState(false);
  const [showWorkSchedule, setShowWorkSchedule] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ personnel_id: '', date: todayIso, leave_type: 'yillik', notes: '' });
  const [leaveMode, setLeaveMode] = useState('single'); // 'single' | 'weekly'
  const [weeklyWeekDate, setWeeklyWeekDate] = useState(todayIso);
  const [weeklyDay, setWeeklyDay] = useState(0); // Pzt=0 ... Paz=6
  const [savingLeave, setSavingLeave] = useState(false);

  const getWeekRange = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = (d.getDay() + 6) % 7; // Pazartesi=0
    const monday = new Date(d); monday.setDate(d.getDate() - day);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return { start: fmt(monday), end: fmt(sunday) };
  };

  const fetchToday = async () => {
    try {
      const res = await axios.get(`${API}/attendance/today`);
      setTodayRecords(res.data || []);
    } catch (e) { console.error('Mesai yüklenemedi', e); }
  };

  const fetchLeaves = async () => {
    try {
      const res = await axios.get(`${API}/personnel-leaves`, { headers: authHeader() });
      setLeaves(res.data || []);
    } catch (e) { /* sessiz */ }
  };

  const fetchQr = async () => {
    try {
      const res = await axios.get(`${API}/attendance/qr-info`);
      setQrToken(res.data?.token || '');
    } catch (e) { console.error('QR yüklenemedi', e); }
  };

  useEffect(() => {
    fetchToday();
    fetchQr();
    fetchLeaves();
    const interval = setInterval(fetchToday, 60000);
    return () => clearInterval(interval);
  }, []);

  // Bugün izinli olan personel: id -> leave_type
  const todayLeaveMap = useMemo(() => {
    const m = {};
    (leaves || []).forEach(l => {
      if (l.approved !== false && l.start_date <= todayIso && l.end_date >= todayIso) {
        m[l.personnel_id] = l.leave_type;
      }
    });
    return m;
  }, [leaves, todayIso]);

  const activePersonnel = useMemo(
    () => personnelList.filter(p => p.active !== false),
    [personnelList]
  );

  const openMap = useMemo(() => {
    const m = {};
    todayRecords.forEach(r => { if (!r.check_out_time) m[r.personnel_id] = r; });
    return m;
  }, [todayRecords]);

  const todayMap = useMemo(() => {
    const m = {};
    todayRecords.forEach(r => {
      if (!m[r.personnel_id]) m[r.personnel_id] = [];
      m[r.personnel_id].push(r);
    });
    return m;
  }, [todayRecords]);

  const stats = useMemo(() => {
    const checkedIn = Object.keys(openMap).length;
    const totalMinutes = todayRecords.reduce((s, r) => s + (r.duration_minutes || 0), 0);
    const completedRecords = todayRecords.filter(r => r.check_out_time).length;
    const presentSet = new Set(todayRecords.map(r => r.personnel_id));
    return {
      checkedIn,
      completed: completedRecords,
      absent: Math.max(activePersonnel.length - presentSet.size, 0),
      totalHours: Math.floor(totalMinutes / 60),
      totalMinutes: totalMinutes % 60
    };
  }, [todayRecords, openMap, activePersonnel]);

  const handleManualCheck = async (person) => {
    if (!qrToken) { toast.error('QR token yüklenmedi'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/attendance/check`, {
        qr_token: qrToken,
        personnel_id: person.id
      });
      if (res.data?.action === 'check_in') {
        toast.success(`${person.name} - Giriş kaydedildi`);
      } else if (res.data?.action === 'check_out') {
        toast.success(`${person.name} - Çıkış (${res.data.duration_text})`);
      }
      await fetchToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakToggle = async (person) => {
    if (!qrToken) { toast.error('QR token yüklenmedi'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/attendance/break`, {
        qr_token: qrToken,
        personnel_id: person.id
      });
      toast.success(res.data?.action === 'break_start'
        ? `${person.name} molaya çıktı ☕`
        : `${person.name} moladan döndü (${formatDuration(res.data.break_minutes)})`);
      await fetchToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  const submitLeave = async () => {
    if (!leaveForm.personnel_id) { toast.error('Personel seçin'); return; }
    let start, end, ltype;
    if (leaveMode === 'weekly') {
      const { start: wkStart } = getWeekRange(weeklyWeekDate);
      const mon = new Date(wkStart + 'T00:00:00');
      mon.setDate(mon.getDate() + weeklyDay);
      start = `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
      end = start;
      ltype = 'haftalik';
    } else {
      if (!leaveForm.date) { toast.error('Tarih seçin'); return; }
      start = leaveForm.date;
      end = leaveForm.date;
      ltype = leaveForm.leave_type;
    }
    setSavingLeave(true);
    try {
      await axios.post(`${API}/personnel-leaves`, {
        personnel_id: leaveForm.personnel_id,
        start_date: start,
        end_date: end,
        leave_type: ltype,
        notes: leaveForm.notes,
        approved: true,
      }, { headers: authHeader() });
      toast.success(leaveMode === 'weekly' ? 'Haftalık izin günü kaydedildi' : 'İzinli gün kaydedildi');
      setShowLeaveModal(false);
      setLeaveForm({ personnel_id: '', date: todayIso, leave_type: 'yillik', notes: '' });
      setLeaveMode('single');
      await fetchLeaves();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İzin kaydedilemedi');
    } finally {
      setSavingLeave(false);
    }
  };

  const handleDelete = async (recordId) => {    if (!window.confirm('Bu mesai kaydını silmek istiyor musunuz?')) return;
    try {
      await axios.delete(`${API}/attendance/${recordId}`);
      toast.success('Kayıt silindi');
      await fetchToday();
    } catch { toast.error('Silinemedi'); }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(qrToken).then(() => toast.success('Kopyalandı'));
  };

  const printA4Qr = () => {
    if (!qrToken) { toast.error('QR token yüklenmedi'); return; }
    const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=12&data=${encodeURIComponent(qrToken)}`;
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) { toast.error('Pop-up engellendi — tarayıcı pop-up iznini açın'); return; }
    const dateStr = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Mesai QR — A4</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #0f172a; }
  .sheet { width: 210mm; min-height: 297mm; padding: 16mm 16mm 12mm; display: flex; flex-direction: column; }
  .brand { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #0e7490; padding-bottom: 10px; }
  .brand h1 { font-size: 26px; letter-spacing: 1px; color: #0e7490; font-weight: 800; }
  .brand .sub { font-size: 11px; color: #64748b; letter-spacing: 3px; text-transform: uppercase; }
  .brand .tag { background: #0e7490; color: #fff; font-size: 12px; font-weight: 700; padding: 6px 14px; border-radius: 999px; }
  .title { text-align: center; margin-top: 22px; }
  .title h2 { font-size: 40px; font-weight: 900; color: #0f172a; line-height: 1.05; }
  .title p { font-size: 16px; color: #0e7490; font-weight: 600; margin-top: 6px; }
  .qrwrap { display: flex; justify-content: center; margin: 26px 0 14px; }
  .qrbox { border: 6px solid #0e7490; border-radius: 22px; padding: 16px; background: #fff; box-shadow: 0 8px 24px rgba(14,116,144,.18); }
  .qrbox img { display: block; width: 300px; height: 300px; }
  .scanhint { text-align: center; font-size: 17px; font-weight: 700; color: #0f172a; margin-bottom: 22px; }
  .scanhint span { color: #0e7490; }
  .steps { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; margin-top: 4px; }
  .step { display: flex; gap: 12px; align-items: flex-start; background: #f0fdff; border: 1px solid #cffafe; border-radius: 14px; padding: 14px 16px; }
  .step .n { flex: 0 0 34px; height: 34px; background: #0e7490; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; }
  .step .t { font-size: 14px; line-height: 1.4; }
  .step .t b { display: block; font-size: 15px; margin-bottom: 2px; color: #0e7490; }
  .note { margin-top: 20px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 14px; padding: 16px 18px; font-size: 13px; color: #92400e; line-height: 1.6; }
  .note b { color: #b45309; }
  .footer { margin-top: auto; padding-top: 18px; border-top: 2px dashed #cbd5e1; display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #64748b; }
  .footer .big { font-size: 13px; font-weight: 700; color: #0e7490; }
</style></head>
<body>
  <div class="sheet">
    <div class="brand">
      <div>
        <h1>AVRASYA SPORTİF</h1>
        <div class="sub">Sportif Faaliyetler</div>
      </div>
      <div class="tag">MESAİ TAKİP · PDKS</div>
    </div>

    <div class="title">
      <h2>MESAİ QR KODU</h2>
      <p>Giriş · Çıkış · Mola işlemleri için okutun</p>
    </div>

    <div class="qrwrap">
      <div class="qrbox"><img src="${qrImg}" alt="QR"/></div>
    </div>
    <div class="scanhint">Telefonunuzla uygulamaya girip <span>bu kodu okutun</span></div>

    <div class="steps">
      <div class="step"><div class="n">1</div><div class="t"><b>Uygulamaya girin</b>Kendi kullanıcı adı ve şifrenizle personel panelinize giriş yapın.</div></div>
      <div class="step"><div class="n">2</div><div class="t"><b>Giriş yapın</b>Mesaiye başlarken "GİRİŞ YAP" ile bu QR kodu okutun.</div></div>
      <div class="step"><div class="n">3</div><div class="t"><b>Molaya çıkın</b>Mola verirken "MOLAYA ÇIK", dönerken "MOLADAN DÖN" ile okutun.</div></div>
      <div class="step"><div class="n">4</div><div class="t"><b>Çıkış yapın</b>Mesai bitiminde "ÇIKIŞ YAP" ile QR kodu tekrar okutun.</div></div>
    </div>

    <div class="note">
      <b>Önemli:</b> QR kodu yalnızca ofis girişinde okutun. Gece yarısını geçen vardiyalarda ertesi gün çıkış yapabilirsiniz — sistem açık mesainizi otomatik kapatır. Mola süreniz ayrı takip edilir. Sorun yaşarsanız yöneticinize bildirin; kayıtlar manuel de düzenlenebilir.
    </div>

    <div class="footer">
      <div><span class="big">Avrasya Sportif Faaliyetler</span> — Personel Mesai Takip Sistemi</div>
      <div>Baskı: ${dateStr}</div>
    </div>
  </div>
  <script>
    var img = document.querySelector('.qrbox img');
    function go(){ setTimeout(function(){ window.focus(); window.print(); }, 300); }
    if (img.complete) { go(); } else { img.onload = go; img.onerror = go; }
  </script>
</body></html>`);
    w.document.close();
  };

  // ==== Rapor fonksiyonları ====
  const fetchReport = async () => {
    setReportLoading(true);
    try {
      let params;
      if (reportPeriod === 'week') {
        const { start, end } = getWeekRange(reportWeekDate);
        params = { start, end };
      } else {
        params = { month: reportMonth };
      }
      const res = await axios.get(`${API}/attendance/report`, { params });
      setReportData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rapor yüklenemedi');
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  const openReport = async () => {
    setShowReportModal(true);
    await fetchReport();
  };

  // Admin: puantaj gün detayında vardiya saatlerini manuel düzenle
  const saveShiftEdit = async () => {
    if (!editingShift?.in) { toast.error('Giriş saati zorunlu'); return; }
    setSavingShift(true);
    try {
      await axios.put(`${API}/attendance/${editingShift.id}/manual`, {
        date: editingShift.date,
        check_in_time: editingShift.in,
        check_out_time: editingShift.out || ''
      }, { headers: authHeader() });
      toast.success('Mesai saatleri güncellendi');
      setEditingShift(null);
      await new Promise(r => setTimeout(r, 250));
      await refreshDayDetail();
      fetchToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    } finally {
      setSavingShift(false);
    }
  };

  // Raporu yeniden çekip açık olan gün detayını günceller
  const refreshDayDetail = async () => {
    try {
      let params;
      if (reportPeriod === 'week') {
        const { start, end } = getWeekRange(reportWeekDate);
        params = { start, end };
      } else {
        params = { month: reportMonth };
      }
      const res = await axios.get(`${API}/attendance/report`, { params });
      setReportData(res.data);
      if (dayDetail) {
        const row = (res.data?.rows || []).find(r => r.personnel_id === dayDetail.row.personnel_id);
        const day = row?.days?.find(d => d.date === dayDetail.day.date);
        if (row && day) {
          setDayDetail({ row, day });
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (showReportModal) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReportModal, reportPeriod, reportMonth, reportWeekDate]);

  const exportReport = async (format) => {
    setExportLoading(true);
    try {
      let params, baseName;
      if (reportPeriod === 'week') {
        const { start, end } = getWeekRange(reportWeekDate);
        params = { start, end, format };
        baseName = `puantaj_${start}_${end}`;
      } else {
        params = { month: reportMonth, format };
        baseName = `puantaj_${reportMonth}`;
      }
      const res = await axios.get(`${API}/attendance/report/export`, {
        params,
        responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${baseName}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} indirildi`);
    } catch (e) {
      toast.error('İndirme başarısız');
    } finally {
      setExportLoading(false);
    }
  };

  const reportMonthLabel = useMemo(() => {
    if (!reportMonth) return '';
    try {
      const [y, m] = reportMonth.split('-');
      const d = new Date(parseInt(y), parseInt(m) - 1, 1);
      return d.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    } catch { return reportMonth; }
  }, [reportMonth]);

  const today = new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-4" data-testid="attendance-panel-root">
      <PageHeader
        icon={Clock}
        title="Mesai (PDKS)"
        description={today}
        testid="attendance-page-header"
        actions={
          <>
            <Button onClick={fetchToday} variant="outline" size="sm" className="h-9" data-testid="refresh-attendance-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Yenile
            </Button>
            <Button onClick={openReport} variant="outline" size="sm" className="h-9" data-testid="open-report-btn">
              <BarChart3 className="w-4 h-4 mr-1.5" /> Aylık Rapor
            </Button>
            {isAdmin && (
              <Button onClick={() => setShowManualAddModal(true)} variant="outline" size="sm" className="h-9" data-testid="manual-add-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Manuel Kayıt
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => setShowLeaveModal(true)} variant="outline" size="sm" className="h-9" data-testid="add-leave-day-btn">
                <Palmtree className="w-4 h-4 mr-1.5" /> İzinli Gün
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => setShowLeaveCalendar(true)} variant="outline" size="sm" className="h-9" data-testid="leave-calendar-btn">
                <CalendarDays className="w-4 h-4 mr-1.5" /> İzin Takvimi
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => setShowWorkSchedule(true)} variant="outline" size="sm" className="h-9" data-testid="work-schedule-btn">
                <Clock className="w-4 h-4 mr-1.5" /> Çalışma Saatleri
              </Button>
            )}
            <Button onClick={() => setShowQrModal(true)} size="sm" className="h-9 bg-[#0B172A] hover:bg-[#13233C]" data-testid="show-qr-btn">
              <QrCode className="w-4 h-4 mr-1.5" /> QR Kod
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={LogIn} tone="emerald" testid="kpi-checked-in" label="İçeride" value={stats.checkedIn} sub="şu an çalışıyor" />
        <StatCard icon={LogOut} tone="slate" testid="kpi-checked-out" label="Çıkış Yapan" value={stats.completed} sub="vardiya tamamlandı" />
        <StatCard icon={AlertCircle} tone="rose" testid="kpi-absent" label="Gelmeyen" value={stats.absent} sub="aktif personelden" />
        <StatCard icon={Timer} tone="sky" testid="kpi-total-hours" label="Bugünkü Toplam" value={`${stats.totalHours}sa ${stats.totalMinutes}dk`} sub="toplam mesai" />
      </div>

      {/* Personnel Cards */}
      <Card className="border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3 border-b border-[#E2E8F0]">
          <CardTitle className="text-base flex items-center gap-2 text-slate-800">
            <Users className="w-4 h-4 text-[#176B91]" /> Personel Mesai Durumu
            <Badge className="ml-2 text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-100">{activePersonnel.length} aktif</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activePersonnel.length === 0 ? (
            <p className="text-center py-6 text-slate-400 text-sm">Aktif personel yok</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="attendance-cards-grid">
              {activePersonnel.map(person => {
                const open = openMap[person.id];
                const records = todayMap[person.id] || [];
                const isInside = !!open;
                const totalToday = records.reduce((s, r) => s + (r.duration_minutes || 0), 0);
                const totalBreak = records.reduce((s, r) => s + (r.break_minutes || 0), 0);
                const onBreak = records.some(r => (r.breaks || []).some(b => b.start && !b.end));
                const onLeave = todayLeaveMap[person.id];
                const lateRec = records.find(r => r.is_late);
                const earlyRec = records.find(r => r.is_early);
                return (
                  <div
                    key={person.id}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      isInside ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`attendance-card-${person.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow ${
                        isInside ? 'bg-emerald-600' : 'bg-[#0B172A]'
                      }`}>
                        {initials(person.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate text-sm">{person.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{person.position || 'Personel'}</p>
                      </div>
                      {isInside ? (
                        <div className="flex flex-col items-end gap-1">
                          <Badge className="bg-emerald-600 text-white border-0 text-[10px]">
                            <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse"></span>
                            İçeride
                          </Badge>
                          {onBreak && (
                            <Badge className="bg-amber-500 text-white border-0 text-[10px] gap-0.5"><Coffee className="w-2.5 h-2.5" /> Molada</Badge>
                          )}
                        </div>
                      ) : onLeave ? (
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] gap-0.5"><Palmtree className="w-2.5 h-2.5" /> İzinli</Badge>
                      ) : records.length > 0 ? (
                        <Badge className="bg-slate-200 text-slate-700 border-0 text-[10px]">Çıktı</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Gelmedi</Badge>
                      )}
                    </div>

                    {records.length > 0 && (
                      <div className="bg-white/70 rounded p-2 mb-2 text-xs space-y-1">
                        {records.map(r => (
                          <div key={r.id} className="flex items-center justify-between gap-1 text-slate-600">
                            <span className="flex items-center">
                              <LogIn className="w-3 h-3 inline mr-0.5 text-emerald-600" />
                              <span className="font-mono">{formatTime(r.check_in_time)}</span>
                              {r.check_out_time && (
                                <>
                                  <span className="mx-1 text-slate-300">→</span>
                                  <LogOut className="w-3 h-3 inline mr-0.5 text-slate-500" />
                                  <span className="font-mono">{formatTime(r.check_out_time)}</span>
                                </>
                              )}
                            </span>
                            <div className="flex items-center gap-1">
                              {r.duration_minutes != null && (
                                <span className="font-semibold text-blue-700 whitespace-nowrap">{formatDuration(r.duration_minutes)}</span>
                              )}
                              {isSuperAdmin && (
                                <Button size="sm" variant="ghost" onClick={() => openEditRecord(r, person.name)} className="h-5 w-5 p-0" title="Saatleri düzenle" data-testid={`edit-attendance-${r.id}`}>
                                  <Edit className="w-3 h-3 text-blue-500" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} className="h-5 w-5 p-0">
                                <Trash2 className="w-3 h-3 text-rose-400" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {records.length > 1 && (
                          <div className="border-t pt-1 mt-1 flex justify-between font-semibold text-slate-700">
                            <span>Toplam</span>
                            <span className="text-blue-700">{formatDuration(totalToday)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      size="sm"
                      onClick={() => handleManualCheck(person)}
                      disabled={loading}
                      className={`w-full ${
                        isInside
                          ? 'bg-rose-600 hover:bg-rose-700 text-white'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                      data-testid={`toggle-attendance-${person.id}`}
                    >
                      {isInside ? (
                        <><LogOut className="w-3.5 h-3.5 mr-1" /> Çıkış Yap</>
                      ) : (
                        <><LogIn className="w-3.5 h-3.5 mr-1" /> Giriş Yap</>
                      )}
                    </Button>
                    {isInside && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBreakToggle(person)}
                        disabled={loading}
                        className="w-full mt-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`break-toggle-${person.id}`}
                      >
                        <Coffee className="w-3.5 h-3.5 mr-1" /> {onBreak ? 'Moladan Döndür' : 'Molaya Çıkar'}
                      </Button>
                    )}
                    {totalBreak > 0 && (
                      <p className="text-[10px] text-amber-600 text-center mt-1" data-testid={`break-total-${person.id}`}>Bugünkü mola: {formatDuration(totalBreak)}</p>
                    )}
                    {lateRec && (
                      <p className="text-[10px] text-rose-600 text-center mt-1 truncate" title={lateRec.late_reason || ''} data-testid={`late-note-${person.id}`}>
                        ⏰ Geç giriş{lateRec.scheduled_start ? ` (${lateRec.scheduled_start})` : ''}{lateRec.late_reason ? `: ${lateRec.late_reason}` : ''}
                      </p>
                    )}
                    {earlyRec && (
                      <p className="text-[10px] text-orange-600 text-center mt-0.5 truncate" title={earlyRec.early_leave_reason || ''} data-testid={`early-note-${person.id}`}>
                        🏃 Erken çıkış{earlyRec.scheduled_end ? ` (${earlyRec.scheduled_end})` : ''}{earlyRec.early_leave_reason ? `: ${earlyRec.early_leave_reason}` : ''}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aylık Rapor Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <BarChart3 className="w-5 h-5 text-cyan-700" /> Aylık Puantaj Raporu
            </DialogTitle>
            <DialogDescription>Personel mesai özeti, günlük dağılım ve bordrolama için Excel/PDF dışa aktarımı</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Kontrol Bar */}
            <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-50 rounded-lg border">
              <div>
                <Label className="text-xs">Dönem Türü</Label>
                <div className="flex rounded-md overflow-hidden border h-9 mt-0.5" data-testid="report-period-toggle">
                  <button
                    type="button"
                    onClick={() => setReportPeriod('month')}
                    className={`px-3 text-sm font-medium transition-colors ${reportPeriod === 'month' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                    data-testid="report-period-month"
                  >Aylık</button>
                  <button
                    type="button"
                    onClick={() => setReportPeriod('week')}
                    className={`px-3 text-sm font-medium transition-colors ${reportPeriod === 'week' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                    data-testid="report-period-week"
                  >Haftalık</button>
                </div>
              </div>
              {reportPeriod === 'month' ? (
                <div>
                  <Label className="text-xs">Ay Seç</Label>
                  <Input
                    type="month"
                    value={reportMonth}
                    onChange={(e) => setReportMonth(e.target.value)}
                    className="h-9 w-44"
                    data-testid="report-month-input"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Hafta (bir gün seçin)</Label>
                  <Input
                    type="date"
                    value={reportWeekDate}
                    onChange={(e) => setReportWeekDate(e.target.value)}
                    className="h-9 w-44"
                    data-testid="report-week-input"
                  />
                </div>
              )}
              <div className="flex-1 min-w-[150px]">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Dönem</p>
                <p className="font-bold text-slate-800 capitalize" data-testid="report-period-label">{reportData?.label || '-'}</p>
              </div>
              <Button
                onClick={() => exportReport('excel')}
                disabled={exportLoading || reportLoading}
                className="bg-emerald-600 hover:bg-emerald-700 h-9"
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel İndir
              </Button>
              <Button
                onClick={() => exportReport('pdf')}
                disabled={exportLoading || reportLoading}
                className="bg-rose-600 hover:bg-rose-700 h-9"
                data-testid="export-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1" /> PDF İndir
              </Button>
            </div>

            {reportLoading ? (
              <p className="text-center py-12 text-slate-400">Rapor yükleniyor...</p>
            ) : reportData ? (
              <>
                {/* Özet Kartları */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card className="border-l-4 border-l-cyan-500">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Personel</p>
                      <p className="text-xl font-bold text-cyan-700">{reportData.total_personnel}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Toplam Mesai</p>
                      <p className="text-xl font-bold text-blue-700">
                        {Math.floor(reportData.total_minutes / 60)}sa {reportData.total_minutes % 60}dk
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Çalışılan G/A</p>
                      <p className="text-xl font-bold text-emerald-700">
                        {reportData.rows.reduce((s, r) => s + r.worked_days, 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Toplam Mola</p>
                      <p className="text-xl font-bold text-amber-700">
                        {Math.floor((reportData.total_break_minutes || 0) / 60)}sa {(reportData.total_break_minutes || 0) % 60}dk
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Puantaj Tablosu */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto" style={{ maxHeight: '50vh' }}>
                    <table className="text-xs border-collapse w-full" data-testid="puantaj-table">
                      <thead className="bg-slate-800 text-white sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-2 text-left whitespace-nowrap sticky left-0 bg-slate-800 z-20" style={{ minWidth: '160px' }}>Personel</th>
                          {reportData.dates.map((iso) => {
                            const dt = new Date(iso + 'T00:00:00');
                            const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                            return (
                              <th key={iso} className={`px-1 py-2 text-center ${isWeekend ? 'bg-rose-700' : ''}`} style={{ minWidth: '64px' }}>
                                <div>{dt.getDate()}</div>
                                <div className="text-[9px] opacity-70">{['Pz','Pt','Sa','Ça','Pe','Cu','Ct'][dt.getDay()]}</div>
                              </th>
                            );
                          })}
                          <th className="px-2 py-2 text-center bg-blue-800">Gün</th>
                          <th className="px-2 py-2 text-center bg-blue-800">Saat</th>
                          <th className="px-2 py-2 text-center bg-amber-700">Mola</th>
                          <th className="px-2 py-2 text-center bg-blue-800">İzin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.rows.length === 0 ? (
                          <tr><td colSpan={reportData.dates.length + 5} className="text-center py-6 text-slate-400">Bu dönemde kayıt bulunamadı</td></tr>
                        ) : reportData.rows.map((row, idx) => (
                          <tr key={row.personnel_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-2 py-1.5 font-medium whitespace-nowrap sticky left-0 bg-inherit border-r">
                              <div className="text-slate-800">{row.personnel_name}</div>
                              <div className="text-[10px] text-slate-400">{row.position || '-'}</div>
                            </td>
                            {row.daily.map((d) => {
                              const dt = new Date(d.date + 'T00:00:00');
                              const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                              const mins = d.minutes;
                              const shifts = d.shifts || [];
                              const hasShifts = shifts.length > 0;
                              const firstIn = hasShifts ? shifts[0].in : null;
                              const lastOut = hasShifts ? shifts[shifts.length - 1].out : null;
                              const tooltip = hasShifts
                                ? shifts.map((s) => `${s.in || '?'} → ${s.out || 'açık'}`).join('\n') + (d.date ? `\n\n${d.date}` : '')
                                : (d.on_leave ? `İzinli (${d.leave_type || ''})\n${d.date}` : d.date);
                              return (
                                <td
                                  key={d.date}
                                  onClick={() => setDayDetail({ row, day: d })}
                                  className={`px-0.5 py-1 text-center text-[10px] border-l align-middle cursor-pointer hover:bg-cyan-50 ${d.on_leave && !hasShifts ? 'bg-blue-50' : isWeekend ? 'bg-rose-50' : ''} ${
                                    mins > 0 ? 'text-emerald-700' : d.open ? 'text-amber-600' : 'text-slate-300'
                                  }`}
                                  title={tooltip}
                                  data-testid={`puantaj-cell-${row.personnel_id}-${d.date}`}
                                >
                                  {d.on_leave && !hasShifts ? (
                                    <span className="text-blue-600 font-bold" title="İzinli">İ</span>
                                  ) : hasShifts ? (
                                    <div className="leading-tight">
                                      <div className="text-[9px] tabular-nums text-slate-500">
                                        {firstIn || '?'}
                                        <span className="text-slate-300">→</span>
                                        {lastOut || <span className="text-amber-500">açık</span>}
                                      </div>
                                      <div className="font-semibold tabular-nums">
                                        {mins > 0 ? `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}` : '•'}
                                        {shifts.length > 1 && <span className="text-[8px] text-slate-400 ml-0.5">({shifts.length}x)</span>}
                                      </div>
                                      {d.break_minutes > 0 && (
                                        <div className="text-[8px] text-amber-600 tabular-nums">☕{Math.floor(d.break_minutes / 60)}:{String(d.break_minutes % 60).padStart(2, '0')}</div>
                                      )}
                                    </div>
                                  ) : d.open ? '•' : '-'}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-center font-bold text-emerald-700 bg-emerald-50">{row.worked_days}</td>
                            <td className="px-2 py-1.5 text-center font-bold text-blue-700 bg-blue-50 whitespace-nowrap">
                              {Math.floor(row.total_minutes / 60)}:{String(row.total_minutes % 60).padStart(2, '0')}
                            </td>
                            <td className="px-2 py-1.5 text-center font-bold text-amber-700 bg-amber-50 whitespace-nowrap">
                              {row.total_break_minutes > 0 ? `${Math.floor(row.total_break_minutes / 60)}:${String(row.total_break_minutes % 60).padStart(2, '0')}` : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center font-bold text-blue-700 bg-blue-50">{row.leave_days || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3 px-1">
                  <span><span className="inline-block w-3 h-3 bg-emerald-100 border border-emerald-300 mr-1"></span>Çalışılan (giriş→çıkış / toplam)</span>
                  <span className="text-cyan-700 font-medium">Bir güne tıklayın → gün raporu (giriş/çıkış, molalar, açıklamalar)</span>
                  <span><span className="inline-block w-3 h-3 bg-amber-100 border border-amber-300 mr-1"></span>"açık" = vardiya kapanmamış</span>
                  <span><span className="inline-block w-3 h-3 bg-rose-50 border border-rose-200 mr-1"></span>Hafta sonu</span>
                  <span><span className="inline-block w-3 h-3 bg-blue-100 border border-blue-300 mr-1"></span><strong className="text-blue-600">İ</strong> = İzinli</span>
                  <span className="text-amber-600"><strong>☕</strong> = mola süresi</span>
                  <span className="text-slate-400"><strong>(2x)</strong> = aynı gün birden fazla vardiya</span>
                </div>
              </>
            ) : (
              <p className="text-center py-12 text-slate-400">Rapor verisi yok</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Gün Detay Modal — puantaj hücresine tıklayınca */}
      <Dialog open={!!dayDetail} onOpenChange={(v) => { if (!v) setDayDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="day-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CalendarDays className="w-5 h-5 text-cyan-700" /> Gün Raporu
            </DialogTitle>
            <DialogDescription className="text-xs">
              {dayDetail?.row?.personnel_name} — {dayDetail?.day?.date
                ? new Date(dayDetail.day.date + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
                : ''}
            </DialogDescription>
          </DialogHeader>

          {dayDetail && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border bg-emerald-50 p-2 text-center">
                  <p className="text-[10px] text-slate-500 uppercase">Net Mesai</p>
                  <p className="text-base font-bold text-emerald-700">{formatDuration(dayDetail.day.minutes || 0)}</p>
                </div>
                <div className="rounded-lg border bg-amber-50 p-2 text-center">
                  <p className="text-[10px] text-slate-500 uppercase">Mola</p>
                  <p className="text-base font-bold text-amber-700">{formatDuration(dayDetail.day.break_minutes || 0)}</p>
                </div>
                <div className="rounded-lg border bg-blue-50 p-2 text-center">
                  <p className="text-[10px] text-slate-500 uppercase">Vardiya</p>
                  <p className="text-base font-bold text-blue-700">{(dayDetail.day.shifts || []).length}</p>
                </div>
              </div>

              {dayDetail.day.on_leave && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800 flex items-center gap-2">
                  <Palmtree className="w-4 h-4" /> Bu gün izinli ({dayDetail.day.leave_type || 'izin'})
                </div>
              )}

              {(dayDetail.day.shifts || []).length === 0 ? (
                <p className="text-center py-6 text-sm text-slate-400" data-testid="day-detail-empty">
                  {dayDetail.day.on_leave ? 'İzinli olduğu için mesai kaydı yok' : 'Bu gün mesai kaydı bulunmuyor'}
                </p>
              ) : (
                <div className="space-y-2">
                  {dayDetail.day.shifts.map((s, i) => (
                    <div key={s.id || i} className="rounded-lg border p-3 space-y-2" data-testid={`day-detail-shift-${i}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-700">{(dayDetail.day.shifts.length > 1 ? `${i + 1}. Vardiya` : 'Vardiya')}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-500">{formatDuration(s.minutes || 0)}</span>
                          {isSuperAdmin && s.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-cyan-700 hover:bg-cyan-50"
                              onClick={() => setEditingShift({ id: s.id, in: s.in || '', out: s.out || '', date: dayDetail.day.date })}
                              data-testid={`day-detail-edit-${i}`}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                            </Button>
                          )}
                        </div>
                      </div>

                      {isSuperAdmin && editingShift?.id === s.id && (
                        <div className="rounded-lg border border-cyan-300 bg-cyan-50/70 p-3 space-y-2" data-testid={`day-detail-edit-form-${i}`}>
                          <p className="text-xs font-semibold text-cyan-900">Saatleri manuel düzenle (sadece admin)</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[11px]">Giriş</Label>
                              <Input
                                type="time"
                                value={editingShift.in}
                                onChange={(e) => setEditingShift({ ...editingShift, in: e.target.value })}
                                className="h-9"
                                data-testid="day-detail-checkin-input"
                              />
                            </div>
                            <div>
                              <Label className="text-[11px]">Çıkış <span className="text-slate-400">(boş = açık vardiya)</span></Label>
                              <Input
                                type="time"
                                value={editingShift.out}
                                onChange={(e) => setEditingShift({ ...editingShift, out: e.target.value })}
                                className="h-9"
                                data-testid="day-detail-checkout-input"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveShiftEdit} disabled={savingShift} className="bg-cyan-700 hover:bg-cyan-800 flex-1" data-testid="day-detail-save-btn">
                              {savingShift ? 'Kaydediliyor...' : 'Kaydet'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingShift(null)} data-testid="day-detail-cancel-btn">
                              İptal
                            </Button>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded bg-slate-50 p-2">
                          <p className="text-[10px] text-slate-500 flex items-center gap-1"><LogIn className="w-3 h-3" /> Giriş</p>
                          <p className="font-bold text-slate-800 tabular-nums">{s.in || '-'}</p>
                          {s.scheduled_start && <p className="text-[10px] text-slate-400">program: {s.scheduled_start}</p>}
                        </div>
                        <div className="rounded bg-slate-50 p-2">
                          <p className="text-[10px] text-slate-500 flex items-center gap-1"><LogOut className="w-3 h-3" /> Çıkış</p>
                          <p className="font-bold text-slate-800 tabular-nums">{s.out || <span className="text-amber-600">açık</span>}</p>
                          {s.scheduled_end && <p className="text-[10px] text-slate-400">program: {s.scheduled_end}</p>}
                        </div>
                      </div>

                      {s.is_late && (
                        <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs" data-testid={`day-detail-late-${i}`}>
                          <p className="font-semibold text-rose-700 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> Geç giriş{s.scheduled_start ? ` (mesai ${s.scheduled_start})` : ''}
                          </p>
                          <p className="text-rose-800 mt-0.5">{s.late_reason || 'Açıklama girilmemiş'}</p>
                        </div>
                      )}
                      {s.is_early && (
                        <div className="rounded border border-orange-200 bg-orange-50 p-2 text-xs" data-testid={`day-detail-early-${i}`}>
                          <p className="font-semibold text-orange-700 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" /> Erken çıkış{s.scheduled_end ? ` (bitiş ${s.scheduled_end})` : ''}
                          </p>
                          <p className="text-orange-800 mt-0.5">{s.early_leave_reason || 'Açıklama girilmemiş'}</p>
                        </div>
                      )}

                      <div>
                        <p className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 mb-1">
                          <Coffee className="w-3.5 h-3.5 text-amber-600" /> Molalar {s.break_minutes ? `(toplam ${formatDuration(s.break_minutes)})` : ''}
                        </p>
                        {(s.breaks || []).length === 0 ? (
                          <p className="text-xs text-slate-400">Mola kaydı yok</p>
                        ) : (
                          <div className="divide-y">
                            {s.breaks.map((b, bi) => (
                              <div key={bi} className="flex items-center justify-between py-1 text-xs" data-testid={`day-detail-break-${i}-${bi}`}>
                                <span className="tabular-nums text-slate-700">{b.start || '?'} → {b.end || <span className="text-amber-600">devam ediyor</span>}</span>
                                <span className="font-mono text-amber-700">{formatDuration(b.minutes || 0)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {s.notes && (
                        <p className="text-xs text-slate-500 border-t pt-1.5"><strong>Not:</strong> {s.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="max-w-md" data-testid="qr-info-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" /> Ofis QR Kodu</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 border rounded-lg p-4 text-center">
              {qrToken ? (
                <div className="w-48 h-48 mx-auto bg-white p-2 rounded border-2 border-slate-300 flex items-center justify-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrToken)}`}
                    alt="QR Code"
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <p className="text-slate-400">Token yükleniyor...</p>
              )}
              <div className="mt-3 flex items-center gap-1">
                <p className="flex-1 font-mono text-xs text-slate-600 break-all bg-white p-2 rounded border">{qrToken || '-'}</p>
                <Button size="sm" variant="outline" onClick={copyToken} className="h-9">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800 leading-relaxed">
              💡 Bu QR kodu çıktısını alıp <strong>ofis girişine</strong> asın. Personel kendi telefonundan uygulamaya giriş yapıp QR'ı okutarak <strong>giriş/çıkış</strong> yapabilir. Aynı gün içinde ikinci kez okutmak otomatik <strong>çıkış</strong> kaydeder.
            </div>
            <Button onClick={printA4Qr} className="w-full bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="print-a4-qr-btn">
              <FileText className="w-4 h-4 mr-2" /> A4 Bilgilendirme Çıktısı Yazdır
            </Button>
            <Button onClick={() => setShowQrModal(false)} variant="outline" className="w-full">Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: Mesai Kaydı Düzenleme Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md" data-testid="edit-attendance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit className="w-5 h-5" /> Mesai Saatlerini Düzenle
            </DialogTitle>
            <DialogDescription>
              {editingRecord?.personnel_name ? `${editingRecord.personnel_name} — giriş/çıkış saatlerini güncelleyin` : 'Saatleri düzenleyin'}
            </DialogDescription>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Tarih</Label>
                <Input
                  type="date"
                  value={editingRecord.date}
                  onChange={(e) => setEditingRecord({ ...editingRecord, date: e.target.value })}
                  data-testid="edit-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Giriş Saati *</Label>
                  <Input
                    type="time"
                    value={editingRecord.check_in_HHMM}
                    onChange={(e) => setEditingRecord({ ...editingRecord, check_in_HHMM: e.target.value })}
                    data-testid="edit-checkin-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Çıkış Saati <span className="text-slate-400 font-normal">(boş = açık vardiya)</span></Label>
                  <Input
                    type="time"
                    value={editingRecord.check_out_HHMM}
                    onChange={(e) => setEditingRecord({ ...editingRecord, check_out_HHMM: e.target.value })}
                    data-testid="edit-checkout-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Not</Label>
                <Input
                  value={editingRecord.notes || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, notes: e.target.value })}
                  placeholder="Opsiyonel açıklama"
                  data-testid="edit-notes-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1" disabled={submittingEdit}>
                  İptal
                </Button>
                <Button onClick={submitEdit} disabled={submittingEdit} className="flex-1 bg-[#0B172A] hover:bg-[#13233C]" data-testid="edit-submit-btn">
                  {submittingEdit ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin: Manuel Mesai Kaydı Ekleme Modal */}
      <Dialog open={showManualAddModal} onOpenChange={setShowManualAddModal}>
        <DialogContent className="max-w-md" data-testid="manual-add-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Plus className="w-5 h-5" /> Manuel Mesai Kaydı
            </DialogTitle>
            <DialogDescription>QR'sız geçmiş veya unutulan kayıtlar için manuel giriş.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Personel *</Label>
              <select
                value={manualForm.personnel_id}
                onChange={(e) => setManualForm({ ...manualForm, personnel_id: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="manual-personnel-select"
              >
                <option value="">Personel seçin...</option>
                {activePersonnel.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.position ? `— ${p.position}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Tarih *</Label>
              <Input
                type="date"
                value={manualForm.date}
                onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                data-testid="manual-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Giriş Saati *</Label>
                <Input
                  type="time"
                  value={manualForm.check_in_time}
                  onChange={(e) => setManualForm({ ...manualForm, check_in_time: e.target.value })}
                  data-testid="manual-checkin-input"
                />
              </div>
              <div>
                <Label className="text-xs">Çıkış Saati <span className="text-slate-400 font-normal">(boş = açık vardiya)</span></Label>
                <Input
                  type="time"
                  value={manualForm.check_out_time}
                  onChange={(e) => setManualForm({ ...manualForm, check_out_time: e.target.value })}
                  data-testid="manual-checkout-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Not</Label>
              <Input
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="Opsiyonel"
                data-testid="manual-notes-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowManualAddModal(false)} className="flex-1" disabled={submittingEdit}>
                İptal
              </Button>
              <Button onClick={submitManualCreate} disabled={submittingEdit} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="manual-submit-btn">
                {submittingEdit ? 'Oluşturuluyor...' : 'Kayıt Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: İzinli Gün Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="max-w-md" data-testid="leave-day-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Palmtree className="w-5 h-5" /> İzinli Gün İşaretle
            </DialogTitle>
            <DialogDescription>Personelin izinli gününü işaretleyin. "Haftalık İzin Günü" ile o haftanın izin gününü seçebilirsiniz (her hafta farklı olabilir). Puantaj raporunda "İ" olarak görünür.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Personel *</Label>
              <select
                value={leaveForm.personnel_id}
                onChange={(e) => setLeaveForm({ ...leaveForm, personnel_id: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="leave-personnel-select"
              >
                <option value="">Personel seçin...</option>
                {activePersonnel.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.position ? `— ${p.position}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">İzin Şekli</Label>
              <div className="flex rounded-md overflow-hidden border h-9 mt-0.5" data-testid="leave-mode-toggle">
                <button type="button" onClick={() => setLeaveMode('single')} className={`flex-1 text-sm font-medium transition-colors ${leaveMode === 'single' ? 'bg-[#0B172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`} data-testid="leave-mode-single">Tek Gün</button>
                <button type="button" onClick={() => setLeaveMode('weekly')} className={`flex-1 text-sm font-medium transition-colors ${leaveMode === 'weekly' ? 'bg-[#0B172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`} data-testid="leave-mode-weekly">Haftalık İzin Günü</button>
              </div>
            </div>

            {leaveMode === 'single' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tarih *</Label>
                  <Input type="date" value={leaveForm.date} onChange={(e) => setLeaveForm({ ...leaveForm, date: e.target.value })} data-testid="leave-date-input" />
                </div>
                <div>
                  <Label className="text-xs">İzin Türü *</Label>
                  <select value={leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white" data-testid="leave-type-select">
                    <option value="yillik">Yıllık İzin</option>
                    <option value="raporlu">Raporlu</option>
                    <option value="ucretsiz">Ücretsiz İzin</option>
                    <option value="haftalik">Haftalık İzin</option>
                    <option value="diger">Diğer</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Hafta (herhangi bir gün seçin)</Label>
                  <Input type="date" value={weeklyWeekDate} onChange={(e) => setWeeklyWeekDate(e.target.value)} data-testid="leave-week-input" />
                  <p className="text-[11px] text-slate-500 mt-1">Seçili hafta: <strong>{(() => { const r = getWeekRange(weeklyWeekDate); return `${r.start} — ${r.end}`; })()}</strong></p>
                </div>
                <div>
                  <Label className="text-xs">Bu haftanın izin günü *</Label>
                  <div className="grid grid-cols-7 gap-1 mt-1" data-testid="leave-weekday-picker">
                    {['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'].map((d, i) => (
                      <button key={i} type="button" onClick={() => setWeeklyDay(i)} className={`h-9 rounded-md text-xs font-semibold transition-colors ${weeklyDay === i ? 'bg-[#0B172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`leave-weekday-${i}`}>{d}</button>
                    ))}
                  </div>
                  <p className="text-[11px] text-blue-600 mt-1">İzinli gün: <strong>{(() => { const r = getWeekRange(weeklyWeekDate); const mon = new Date(r.start + 'T00:00:00'); mon.setDate(mon.getDate() + weeklyDay); return mon.toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long' }); })()}</strong></p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Her hafta farklı gün olabilir — istediğiniz hafta için tekrar seçip kaydedin.</p>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Not</Label>
              <Input
                value={leaveForm.notes}
                onChange={(e) => setLeaveForm({ ...leaveForm, notes: e.target.value })}
                placeholder="Opsiyonel açıklama"
                data-testid="leave-notes-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowLeaveModal(false)} className="flex-1" disabled={savingLeave}>
                İptal
              </Button>
              <Button onClick={submitLeave} disabled={savingLeave} className="flex-1 bg-[#0B172A] hover:bg-[#13233C]" data-testid="leave-submit-btn">
                {savingLeave ? 'Kaydediliyor...' : 'İzinli İşaretle'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <LeaveCalendarModal
        open={showLeaveCalendar}
        onClose={() => setShowLeaveCalendar(false)}
        personnelList={personnelList}
      />

      <WorkScheduleModal
        open={showWorkSchedule}
        onClose={() => setShowWorkSchedule(false)}
        personnelList={personnelList}
      />
    </div>
  );
};

export default AttendancePanel;
